import { useState, useEffect } from "react";
import { useTranslation } from "../../i18n/i18n";

const TransactionForm = ({
	initialData = {},
	onFormChange,
	disabled = false,
	showTypeSelector = true,
	readOnly = false,
	isRecurringEdit = false,
}) => {
	const { translate } = useTranslation();

	// Category and subCategory label mapping
	const categories = {
		food: "category.food",
		general: "category.general",
		personal: "category.personal",
		health: "category.health",
		housing: "category.housing",
		savings: "category.savings",
		income: "category.income",
	};

	const subCategories = {
		food: {
			groceries: "subCategory.food.groceries",
			restaurant: "subCategory.food.restaurant",
			seazon: "subCategory.food.seazon",
		},
		general: {
			transport: "subCategory.general.transport",
			home: "subCategory.general.home",
			utilities: "subCategory.general.utilities",
			donations: "subCategory.general.donations",
			other: "subCategory.general.other",
		},
		personal: {
			vacation: "subCategory.personal.vacation",
			activities: "subCategory.personal.activities",
			shopping: "subCategory.personal.shopping",
			sport: "subCategory.personal.sport",
			subscriptions: "subCategory.personal.subscriptions",
			gifts: "subCategory.personal.gifts",
			other: "subCategory.personal.other",
		},
		health: {
			doctor: "subCategory.health.doctor",
			pharmacy: "subCategory.health.pharmacy",
			cosmectics: "subCategory.health.cosmectics",
			other: "subCategory.health.other",
		},
		housing: {
			loan: "subCategory.housing.loan",
			charges: "subCategory.housing.charges",
			insurance: "subCategory.housing.insurance",
		},
		income: {
			salary: "subCategory.income.salary",
			lunchTickets: "subCategory.income.lunchTickets",
			socialSecurityRefund: "subCategory.income.socialSecurityRefund",
			healthInsuranceRefund: "subCategory.income.healthInsuranceRefund",
			other: "subCategory.income.other",
		},
	};

	const [formData, setFormData] = useState({
		type: "expense",
		title: "",
		amount: "",
		categoryId: "food",
		subCategoryId: "groceries",
		date: new Date().toISOString().split("T")[0],
		...initialData,
	});

	// Get tomorrow's date for validation
	const tomorrow = new Date();
	tomorrow.setDate(tomorrow.getDate() + 1);
	const tomorrowString = tomorrow.toISOString().split("T")[0];

	// Update data when initialData changes
	useEffect(() => {
		if (initialData && Object.keys(initialData).length > 0) {
			setFormData(prev => ({
				...prev,
				...initialData,
			}));
		}
	}, [initialData]);

	// Function to get the first subcategory of a category
	const getFirstSubCategory = categoryId => {
		if (subCategories[categoryId]) {
			return Object.keys(subCategories[categoryId])[0];
		}
		return "";
	};

	// Initialize categories according to type
	useEffect(() => {
		if (formData.type === "income" && !initialData.categoryId) {
			setFormData(prev => ({
				...prev,
				categoryId: "income",
				subCategoryId: "salary",
			}));
		} else if (formData.type !== "income" && !initialData.categoryId) {
			setFormData(prev => ({
				...prev,
				categoryId: "food",
				subCategoryId: getFirstSubCategory("food"),
			}));
		}
	}, [formData.type, initialData.categoryId]);

	// Update subcategory when category changes
	useEffect(() => {
		if (
			formData.categoryId &&
			formData.categoryId !== "income" &&
			!initialData.subCategoryId
		) {
			const firstSubCategory = getFirstSubCategory(formData.categoryId);
			setFormData(prev => ({
				...prev,
				subCategoryId: firstSubCategory,
			}));
		}
	}, [formData.categoryId, initialData.subCategoryId]);

	// Notify the parent when the data changes
	useEffect(() => {
		onFormChange?.(formData);
	}, [formData, onFormChange]);

	const handleChange = (field, value) => {
		setFormData(prev => {
			const newData = { ...prev, [field]: value };

			// Special handling for type changes
			if (field === "type") {
				if (value === "income") {
					newData.categoryId = "income";
					newData.subCategoryId = "salary";
				} else {
					newData.categoryId = "food";
					newData.subCategoryId = getFirstSubCategory("food");
				}
			}

			// Special management for category change
			if (field === "categoryId") {
				newData.subCategoryId = getFirstSubCategory(value);
			}

			return newData;
		});
	};

	return (
		<>
			{/* Type Selection */}
			{showTypeSelector && (
				<div className="layout-row">
					<div className="segmented-controls">
						<input
							type="radio"
							id="expense"
							name="type"
							value="expense"
							checked={formData.type === "expense"}
							onChange={() => handleChange("type", "expense")}
							disabled={disabled || readOnly}
						/>
						<label htmlFor="expense">{translate("form.expense")}</label>
						<input
							type="radio"
							id="income"
							name="type"
							value="income"
							checked={formData.type === "income"}
							onChange={() => handleChange("type", "income")}
							disabled={disabled || readOnly}
						/>
						<label htmlFor="income">{translate("form.income")}</label>
						<span className={`paddle ${formData.type}`} />
					</div>
				</div>
			)}

			{/* Title */}
			<div className="layout-row">
				<div className="layout-col-full">
					<label htmlFor="transactionTitle">{translate("form.title")}</label>
					{readOnly ? (
						<div className="field-display">{formData.title}</div>
					) : (
						<input
							id="transactionTitle"
							className="field"
							type="text"
							placeholder={translate("form.titlePlaceholder")}
							value={formData.title}
							onChange={e => handleChange("title", e.target.value)}
							disabled={disabled}
							required
						/>
					)}
				</div>
			</div>

			{/* Amount and Date */}
			<div className="layout-row">
				<div className="layout-col-1-2 flex-end">
					<label htmlFor="amount">{translate("form.amount")}</label>
					{readOnly ? (
						<div className="field-display">{formData.amount}</div>
					) : (
						<input
							id="amount"
							className="field"
							type="number"
							placeholder={translate("form.amountPlaceholder")}
							step="0.01"
							value={formData.amount}
							onChange={e => handleChange("amount", e.target.value)}
							disabled={disabled}
							required
						/>
					)}
				</div>
				<div className="layout-col-1-2">
					<label htmlFor="date">
						{isRecurringEdit
							? translate("form.nextOccurrence")
							: translate("form.date")}
					</label>
					{readOnly ? (
						<div className="field-display">{formData.date}</div>
					) : (
						<input
							id="date"
							className="field"
							type="date"
							value={formData.date}
							onChange={e => handleChange("date", e.target.value)}
							disabled={disabled}
							// For recurring transaction edits, minimum date is tomorrow
							// For regular transactions or new recurring transactions, no restriction
							min={isRecurringEdit ? tomorrowString : undefined}
							required
						/>
					)}
				</div>
			</div>

			{/* Category - only show if type is not income */}
			{formData.type !== "income" && (
				<div className="layout-row">
					<div className="layout-col-full">
						<label htmlFor="categorySelect">{translate("form.category")}</label>
						{readOnly ? (
							<div className="field-display">
								{translate(categories[formData.categoryId])}
							</div>
						) : (
							<div className="select-wrapper">
								<select
									id="categorySelect"
									className="custom-select"
									value={formData.categoryId}
									onChange={e => handleChange("categoryId", e.target.value)}
									disabled={disabled}
									required
								>
									{Object.keys(categories)
										.filter(id => id !== "income")
										.map(id => (
											<option key={id} value={id}>
												{translate(categories[id])}
											</option>
										))}
								</select>
							</div>
						)}
					</div>
				</div>
			)}

			{/* SubCategory */}
			{formData.categoryId && subCategories[formData.categoryId] && (
				<div className="layout-row">
					<div className="layout-col-full">
						<label htmlFor="subCategorySelect">
							{translate("form.subCategory")}
						</label>
						{readOnly ? (
							<div className="field-display">
								{formData.subCategoryId
									? translate(
											subCategories[formData.categoryId][formData.subCategoryId]
									  )
									: translate("form.noSubCategory")}
							</div>
						) : (
							<div className="select-wrapper">
								<select
									id="subCategorySelect"
									className="custom-select"
									value={formData.subCategoryId}
									onChange={e => handleChange("subCategoryId", e.target.value)}
									disabled={disabled}
								>
									<option value="">{translate("form.selectSubCat")}</option>
									{Object.entries(subCategories[formData.categoryId]).map(
										([id, labelKey]) => (
											<option key={id} value={id}>
												{translate(labelKey)}
											</option>
										)
									)}
								</select>
							</div>
						)}
					</div>
				</div>
			)}
		</>
	);
};

export default TransactionForm;
